import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  BarChart3,
  FileText,
  Users,
  Tag,
  Folder,
  Loader2,
  Search,
  MoreVertical,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdmin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [authorBio, setAuthorBio] = useState('');

  const user = JSON.parse(localStorage.getItem('examapp_user') || '{}');
  const token = localStorage.getItem('examapp_token');
  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    fetchStats();
    fetchPosts();
    fetchCategories();
    if (isSuperAdmin) {
      fetchAuthors();
      fetchAvailableUsers();
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/posts?include_drafts=true&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAuthors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/authors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuthors(data.authors);
      }
    } catch (error) {
      console.error('Error fetching authors:', error);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/users/available`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Post deleted');
        fetchPosts();
        fetchStats();
      } else {
        toast.error('Failed to delete post');
      }
    } catch (error) {
      toast.error('Error deleting post');
    }
  };

  const handleTogglePublish = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      const response = await fetch(`${API_URL}/api/blog/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(newStatus === 'published' ? 'Post published!' : 'Post unpublished');
        fetchPosts();
        fetchStats();
      }
    } catch (error) {
      toast.error('Error updating post');
    }
  };

  const handleSaveCategory = async () => {
    const method = editingCategory ? 'PUT' : 'POST';
    const url = editingCategory 
      ? `${API_URL}/api/blog/categories/${editingCategory.id}`
      : `${API_URL}/api/blog/categories`;

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(categoryForm)
      });
      if (response.ok) {
        toast.success(editingCategory ? 'Category updated' : 'Category created');
        setShowCategoryModal(false);
        setCategoryForm({ name: '', description: '' });
        setEditingCategory(null);
        fetchCategories();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save category');
      }
    } catch (error) {
      toast.error('Error saving category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Posts will be uncategorized.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blog/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Category deleted');
        fetchCategories();
      }
    } catch (error) {
      toast.error('Error deleting category');
    }
  };

  const handleAddAuthor = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/blog/authors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: selectedUserId, bio: authorBio })
      });
      if (response.ok) {
        toast.success('Blog Author added');
        setShowAuthorModal(false);
        setSelectedUserId('');
        setAuthorBio('');
        fetchAuthors();
        fetchAvailableUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add author');
      }
    } catch (error) {
      toast.error('Error adding author');
    }
  };

  const handleRemoveAuthor = async (authorId) => {
    if (!window.confirm('Remove Blog Author role from this user?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/blog/authors/${authorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Blog Author role removed');
        fetchAuthors();
        fetchAvailableUsers();
      }
    } catch (error) {
      toast.error('Error removing author');
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || post.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(isSuperAdmin ? '/super-admin/dashboard' : '/dashboard')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Blog Management</h1>
                <p className="text-sm text-slate-500">Create and manage blog posts</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/super-admin/blog/new')}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="new-post-btn"
            >
              <Plus className="w-4 h-4" />
              New Post
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_posts}</p>
                  <p className="text-xs text-slate-500">Total Posts</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Eye className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.published_posts}</p>
                  <p className="text-xs text-slate-500">Published</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <EyeOff className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.draft_posts}</p>
                  <p className="text-xs text-slate-500">Drafts</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_views}</p>
                  <p className="text-xs text-slate-500">Total Views</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-100 rounded-lg">
                  <Folder className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.categories_count}</p>
                  <p className="text-xs text-slate-500">Categories</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'posts' 
                ? 'text-indigo-600' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-posts"
          >
            Posts
            {activeTab === 'posts' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
            )}
          </button>
          {isSuperAdmin && (
            <>
              <button
                onClick={() => setActiveTab('categories')}
                className={`px-4 py-2 font-medium transition-colors relative ${
                  activeTab === 'categories' 
                    ? 'text-indigo-600' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-categories"
              >
                Categories
                {activeTab === 'categories' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('authors')}
                className={`px-4 py-2 font-medium transition-colors relative ${
                  activeTab === 'authors' 
                    ? 'text-indigo-600' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-authors"
              >
                Authors
                {activeTab === 'authors' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
                )}
              </button>
            </>
          )}
        </div>

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div className="bg-white rounded-xl border border-slate-200">
            {/* Filters */}
            <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="search-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            {/* Posts List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-20">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No posts found</h3>
                <p className="text-slate-500 mb-4">Get started by creating your first blog post</p>
                <button
                  onClick={() => navigate('/super-admin/blog/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4" />
                  Create Post
                </button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredPosts.map((post) => (
                  <div key={post.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`post-row-${post.id}`}>
                    <div className="flex items-start gap-4">
                      {post.featured_image ? (
                        <img
                          src={post.featured_image}
                          alt=""
                          className="w-20 h-14 object-cover rounded-lg bg-slate-100"
                        />
                      ) : (
                        <div className="w-20 h-14 bg-slate-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-medium text-slate-900 truncate">{post.title}</h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                post.status === 'published' 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {post.status}
                              </span>
                              {post.category && (
                                <span className="flex items-center gap-1">
                                  <Folder className="w-3 h-3" />
                                  {post.category.name}
                                </span>
                              )}
                              <span>{formatDate(post.published_at || post.created_at)}</span>
                              {post.view_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {post.view_count}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="View post"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleTogglePublish(post)}
                              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                            >
                              {post.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => navigate(`/super-admin/blog/edit/${post.id}`)}
                              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePost(post.id)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && isSuperAdmin && (
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-semibold text-slate-900">Categories</h3>
              <button
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryForm({ name: '', description: '' });
                  setShowCategoryModal(true);
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                data-testid="add-category-btn"
              >
                <Plus className="w-4 h-4" />
                Add Category
              </button>
            </div>
            {categories.length === 0 ? (
              <div className="text-center py-12">
                <Folder className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No categories yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {categories.map((cat) => (
                  <div key={cat.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div>
                      <h4 className="font-medium text-slate-900">{cat.name}</h4>
                      <p className="text-sm text-slate-500">{cat.description || 'No description'}</p>
                      <p className="text-xs text-slate-400 mt-1">{cat.post_count} posts</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditingCategory(cat);
                          setCategoryForm({ name: cat.name, description: cat.description || '' });
                          setShowCategoryModal(true);
                        }}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Authors Tab */}
        {activeTab === 'authors' && isSuperAdmin && (
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-semibold text-slate-900">Blog Authors</h3>
              <button
                onClick={() => setShowAuthorModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                data-testid="add-author-btn"
              >
                <Plus className="w-4 h-4" />
                Add Author
              </button>
            </div>
            {authors.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No blog authors yet</p>
                <p className="text-sm text-slate-400">Add authors to let them write blog posts</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {authors.map((author) => (
                  <div key={author.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold">
                        {author.name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{author.name}</h4>
                        <p className="text-sm text-slate-500">{author.email}</p>
                        <p className="text-xs text-slate-400 mt-1">{author.post_count} posts written</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveAuthor(author.id)}
                      className="px-3 py-1.5 text-red-600 text-sm border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      Remove Role
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Category name"
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  placeholder="Optional description"
                  data-testid="category-desc-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="save-category-btn"
              >
                {editingCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Author Modal */}
      {showAuthorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Blog Author</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Select User</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="select-user"
                >
                  <option value="">Choose a user...</option>
                  {availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email}) - {u.role}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Author Bio</label>
                <textarea
                  value={authorBio}
                  onChange={(e) => setAuthorBio(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  placeholder="Brief author bio (optional)"
                  data-testid="author-bio-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAuthorModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAuthor}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="add-author-confirm-btn"
              >
                Add Author
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;
